<template>
  <AppDialog
    :is-open="isDialogOpened"
    size="xxxl"
    content-class="ChecklistStatusDialog"
    @cancel="checkModificationsBeforeClose"
  >
    <template #title>{{ $t(`${TRANSLATION_KEY_PREFIX}.title`) }}</template>
    <template #subTitle>{{ displayedSubtitle }}</template>
    <template #body>
      <template v-if="taskStatusCategoriesPending">
        <div class="text-center">
          <AppLoader/>
        </div>
      </template>
      <div class="d-flex flex-column row-gap-4">
        <template v-if="isBulkEdition">
          <app-bulk-list 
            :items="tasks"
            :items-name="$t(`${TRANSLATION_KEY_PREFIX}.bulkListItemsName`)"
          >
            <template #default="{ item }">
              <div class="d-flex align-start column-gap-2">
                <span>{{ item.positionDisplay }}</span> <span>{{ item.title }}</span>
              </div>
            </template>
          </app-bulk-list>
        </template>
        <template v-if="statusCategoriesList.length > 0">
          <div class="d-flex column-gap-4">
            <ChecklistStatusDialogCategory 
              v-for="statusCategory in statusCategoriesList"
              :key="`category-${statusCategory.name}`"
              :category="statusCategory"
              :edit-mode="editMode"
              :is-bulk-edition="isBulkEdition"
              v-model:selected-status-id="selectedStatusId"
              @status-edit="onStatusEdit"
              @status-delete="onStatusDelete"
            />
          </div>
        </template>
      </div>
    </template>
    <template #footer>
      <div class="w-100 d-flex flex-column flex-sm-row align-start align-sm-end row-gap-4">
        <div class="d-flex flex-grow-1 row-gap-4 flex-column align-start justify-end">
          <!-- Send notif -->
          <v-checkbox v-model="taskStatusSendNotif"
            class="d-block"
            :label="$t('project.checklist.dialogs.ChecklistStatusDialog.sendNotifLabel')"
            hide-details
          />

          <app-button v-if="editMode"
            type="outlined"
            variant="neutral"
            :disabled="isTaskEditionPending "
            @click="cancelModifications"
          >
            {{ $t(`${TRANSLATION_KEY_PREFIX}.cancelModificationsBtnText`) }}
          </app-button>
          <app-button v-else-if="!isBulkEdition"
            type="outlined"
            variant="neutral"
            :disabled="isTaskEditionPending "
            @click="checkModificationsBeforeClose"
          >
            {{ $t('common.cancel') }}
          </app-button>
        </div>
        <div class="d-flex flex-column flex-sm-row row-gap-2 column-gap-4 justify-end">
          <template v-if="canCurrentUserEditProjectStatuses">
            <app-button v-if="editMode"
              type="primary"
              :loading="postStatusPending"
              :disabled="isTaskEditionPending "
              @click="preparePostStatus"
            >
              {{ $t(`${TRANSLATION_KEY_PREFIX}.saveStatus`) }}
            </app-button>
            <app-button v-else
              type="outlined"
              variant="neutral"
              :disabled="isTaskEditionPending "
              @click="editMode = true"
            >
              {{ $t(`${TRANSLATION_KEY_PREFIX}.editStatus`) }}
            </app-button>
          </template>
          <app-button v-if="isBulkEdition"
            type="outlined"
            variant="neutral"
            :disabled="isTaskEditionPending"
            @click="checkModificationsBeforeClose"
          >
            {{ $t('common.cancel') }}
          </app-button>
          <AppTooltip :disabled="!isValidateDisabled">
            <template #activator="{ props }">
              <span v-bind="props">
                <app-button
                  type="primary"
                  :disabled="isValidateDisabled"
                  :loading="isTaskEditionPending "
                  @click="onValidate"
                >
                  {{ $t(`${TRANSLATION_KEY_PREFIX}.saveStatus`) }}
                </app-button>
              </span>
            </template>
            <span>{{ $t(`${TRANSLATION_KEY_PREFIX}.validateBtnTooltip`) }}</span>
          </AppTooltip>
        </div>
      </div>

      <ChecklistStatusDialogCloseConfirmation v-if="isBeforeCloseDialogOpen"
                                       @confirm="closeModalWhithoutSaving"
                                       @close="isBeforeCloseDialogOpen = false"
      />
    </template>
  </AppDialog>
</template>

<script>
import { cloneDeep } from 'lodash-es'
import { mapActions, mapGetters, mapState } from 'vuex'

import AppLoader from '@/common/AppLoader.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import AppDialog from '@/common/dialogs/AppDialog.vue'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import ChecklistStatusDialogCategory from '@/project/checklist/dialogs/ChecklistStatusDialogCategory.vue'
import ChecklistStatusDialogCloseConfirmation from '@/project/checklist/dialogs/ChecklistStatusDialogCloseConfirmation.vue'
import { statusCategories } from '@/project/checklist/dialogs/taskStatus'
import {
  EDIT_TASK,
  GET_CHECKLIST,
  GET_TASK_DETAILS,
  GET_TASK_STATUS_CATEGORIES,
  POST_STATUS,
  UPDATE_TASK_STATUSES,
} from '@/store/modules/checklist/action_types'

const TRANSLATION_KEY_PREFIX = 'project.checklist.dialogs.ChecklistStatusDialog'

export default {
  name: 'ChecklistStatusDialog',
  components: {
    AppDialog,
    AppLoader,
    AppTooltip,
    ChecklistStatusDialogCategory,
    ChecklistStatusDialogCloseConfirmation,
  },
  emits: ['clear-selection'],
  mixins: [dialogMixin],
  props: {
    mnemo: {
      type: String,
      required: true,
    },
    tasks: {
      type: Array,
      required: true,
    },
  },
  data () {
    return {
      TRANSLATION_KEY_PREFIX,
      editMode: false,
      internalStatusCategories: null,
      isBeforeCloseDialogOpen: false,
      isPostStatusSuccessful: false,
      selectedStatusId: 0,
      taskStatusSendNotif: true,
    }
  },
  computed: {
    ...mapState('checklist', [
      'editTaskPending',
      'taskStatusCategories',
      'taskStatusCategoriesPending',
      'postStatusPending',
      'updateTaskStatusesPending',
    ]),
    ...mapGetters('groups', ['currentUserGroupId']),
    ...mapGetters('room', ['isCurrentUserPm']),
    ...mapGetters('user', ['currentUserId']),
    canCurrentUserEditProjectStatuses () {
      return this.isCurrentUserPm && !this.isBulkEdition
    },
    currentUserIsResponsibleOfTheTask () {
      return this.tasks.every(task =>
        !!task.rights.responsibles.users.find(user => user.id === this.currentUserId) ||
        !!task.rights.responsibles.groups.find(group => group.id === this.currentUserGroupId),
      )
    },
    currentUserIsValidatorOfTheTask () {
      return this.tasks.every(task =>
        !!task.rights.validators.users.find(user => user.id === this.currentUserId) ||
        !!task.rights.validators.groups.find(group => group.id === this.currentUserGroupId),
      )
    },
    displayedSubtitle () {
      if (this.isBulkEdition) {
        return this.$t(`${TRANSLATION_KEY_PREFIX}.subtitleBulk`)
      }
      return this.$t(`${TRANSLATION_KEY_PREFIX}.subtitle`)
    },
    statusCategoriesList () {
      if (!this.internalStatusCategories) {
        return []
      }

      return Object.entries(this.internalStatusCategories).map(
        ([category, statuses]) => ({
          name: category,
          statuses: statuses,
          areStatusesSelectable: this.areCategoryStatusesSelectable(category),
        }),
      )
    },
    haveTaskStatusesModifications () {
      return this.selectedStatusId !== (this.haveTasksSameStatus ? this.tasks[0].status.id : 0)
      || JSON.stringify(this.internalStatusCategories) !== JSON.stringify(this.taskStatusCategories)
    },
    haveTasksSameStatus () {
      if (this.tasks.length === 0) return false

      return this.tasks.every(task => task.status.id === this.tasks[0].status.id)
    },
    isBulkEdition () {
      return this.selectedTasksCount > 1
    },
    isTaskEditionPending  () {
      return this.updateTaskStatusesPending.some(taskId => this.tasks.map(task => task.id).includes(taskId))
    },
    isValidateDisabled () {
      return !this.haveTaskStatusesModifications || this.editMode || this.isTaskEditionPending
    },
    selectedTasksCount () {
      return this.tasks.length
    },
  },
  watch: {
    taskStatusCategories (categories) {
      this.internalStatusCategories = cloneDeep(categories)
    },
  },
  created () {
    this.selectedStatusId = this.haveTasksSameStatus ? this.tasks[0].status.id : 0
    this.prepareGetTaskStatuses()
  },
  methods: {
    ...mapActions('checklist', [
      EDIT_TASK,
      GET_CHECKLIST,
      GET_TASK_DETAILS,
      GET_TASK_STATUS_CATEGORIES,
      POST_STATUS,
      UPDATE_TASK_STATUSES,
    ]),
    onValidate() {
      this.prepareUpdateTasksStatus()
    },
    async prepareGetTaskStatuses () {
      try {
        await this.GET_TASK_STATUS_CATEGORIES({ mnemo: this.mnemo })
      } catch (error) {
        console.error(error)
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.getTaskStatusesError`))
      }
    },
    async prepareUpdateTasksStatus () {
      const tasksToEdit = this.tasks.filter(task => task.status.id !== this.selectedStatusId)

      try {
        const response = await this.UPDATE_TASK_STATUSES({
          mnemo: this.mnemo,
          data: {
            sendNotif: this.taskStatusSendNotif,
            statusId: this.selectedStatusId,
            taskIds: tasksToEdit.map(task => task.id),
          },
        })

        const failedUpdates = response.results.filter(res => res.status !== 'success')

        if (failedUpdates.length) {
          toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.updateTaskStatusesError`))
        } else {
          this.$emit('clear-selection')
          toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.updateTaskStatusesSuccess`))
        }
        
        if (!this.isBulkEdition) {
          await this.GET_TASK_DETAILS({
            mnemo: this.mnemo,
            taskId: this.tasks[0].id,
          })
        } else {
          await this.GET_CHECKLIST(this.mnemo)
        }

        this.closeDialog()
      } catch (error) {
        console.error(error)
        if (error?.response?.data?.errorCode === 'ERR_INVALID_RIGHTS') {
          toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.invalidRightsError`))
        } else {
          toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.updateTaskStatusesError`))
        }
      }
    },
    async preparePostStatus () {
      this.isPostStatusSuccessful = false

      try {
        this.isPostStatusSuccessful = await this.POST_STATUS({
          mnemo: this.mnemo,
          data: { statuses: this.internalStatusCategories },
        })
      } catch (error) {
        console.error(error)
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.postStatusError`))
      } finally {
        this.editMode = false
      }
    },
    areCategoryStatusesSelectable (category) {
      return this.isCurrentUserPm ||
        this.currentUserIsValidatorOfTheTask ||
        (
          this.currentUserIsResponsibleOfTheTask &&
          [statusCategories.DRAFT, statusCategories.IN_PROGRESS, statusCategories.HELD].includes(category)
        )
    },
    cancelModifications () {
      this.editMode = false
      this.internalStatusCategories = cloneDeep(this.taskStatusCategories)
    },
    checkModificationsBeforeClose () {
      if (this.haveTaskStatusesModifications) {
        this.isBeforeCloseDialogOpen = true
      } else {
        if(this.isPostStatusSuccessful) {
          this.GET_CHECKLIST(this.mnemo)
        }
        this.closeDialog()
      }
    },
    closeModalWhithoutSaving () {
      this.isBeforeCloseDialogOpen = false
      this.closeDialog()
    },
    onStatusDelete ({ status, category }) {
      const statusIndex = this.internalStatusCategories[category].findIndex(s => s.id === status.id)
      if (statusIndex >= 0) {
        this.internalStatusCategories[category].splice(statusIndex, 1)
      }
    },
    onStatusEdit ({ editedCategory, editedStatus }) {
      const statusToEditIndex = this.internalStatusCategories[editedCategory].findIndex(
        status => status.id === editedStatus.id,
      )

      if (statusToEditIndex < 0) {
        return
      }

      if (editedStatus.category === editedCategory) {
        this.internalStatusCategories[editedCategory][statusToEditIndex].label = editedStatus.label
        this.internalStatusCategories[editedCategory][statusToEditIndex].color = editedStatus.color
      } else {
        const statusToEdit = this.internalStatusCategories[editedCategory][statusToEditIndex]
        this.internalStatusCategories[editedStatus.category].push({
          ...cloneDeep(statusToEdit),
          label: editedStatus.label,
          color: editedStatus.color,
        })
        this.internalStatusCategories[editedCategory].splice(statusToEditIndex, 1)
      }
    },
  },
}
</script>

<style lang="scss">
@import "vuetify/settings";
.ChecklistStatusDialog {
  max-height: 600px;
  display: flex;
  overflow: hidden;

  .v-card {
    display: flex;
    flex-direction: column;

    &__text {
      flex: 1;
      display: flex;
      overflow-y: hidden;
    }
  }

  @media #{map-get($display-breakpoints, 'md-and-down')} {
    .v-card-text {
      overflow-x: scroll;
    }
  }
}
</style>
